import React, { useEffect } from 'react';
import { useStore } from '../../../../stores/root';
import moment from 'moment';

// assets
import { faCheck, faXmarkCircle } from '@fortawesome/pro-light-svg-icons';

// services
import ErrorService from '../../../../services/general/error';
import GeneralService from '../../../../services/general';

// props

// components
import { DefaultButton, Panel, PanelType, PrimaryButton, Spinner, SpinnerSize, Stack, TextField } from '@fluentui/react';
import Text from '../../../typography/text';
import Details from '../../../typography/details';
import EmployeeLeaveService from '../../../../services/employees/leave';
import SelectEmployee from '../../../uiframeworks/forms/selectEmployee';
import SelectSubsidiary from '../../../uiframeworks/forms/selectSubsidiary';
import EmployeeLeaveTypes from '../../../../manifests/types/employeeLeave';
import { IEmployeeLeaveApprovalResourceProps } from '../../../../props/employees/leaves/approval';
import ValidationService from '../../../../services/validation';

interface RejectEmployeeLeaveFormProps {
    leaveId: string;
    onDismissed(refresh?: boolean, status?: string): void;
}

const RejectEmployeeLeaveForm: React.FC<RejectEmployeeLeaveFormProps> = (props: RejectEmployeeLeaveFormProps) => {
    const { banner, user } = useStore();
    const [loaded, setLoaded] = React.useState<boolean>(false);
    const [submitting, setSubmitting] = React.useState<boolean>(false);
    const [data, setData] = React.useState<Partial<IEmployeeLeaveApprovalResourceProps>>({
        id: GeneralService.guid()
    });
    const [error, setError] = React.useState<any>({});

    useEffect(() => {
        init();
    }, []);

    const init = async () => {
        try {
            let _data = data;
            const leave = await EmployeeLeaveService.read(props.leaveId);
            _data.leave = leave;

            setData({ ..._data });
            setLoaded(true);
        } catch (e) {
            banner.add({
                key: 'get_leave_error',
                variant: 'error',
                icon: faXmarkCircle,
                text: `Failed to get leave details. Error: ${ErrorService.getMessage(e)}`
            });
            props.onDismissed();
        }
    }

    const isSubmitButtonDisabled = (): boolean => {
        if ((data.justification || "").trim() === "" || error.justification) {
            return true;
        }

        return false;
    }

    const _onSubmit = async () => {
        try {
            const confirmed = window.confirm("Are you sure you want to reject this leave?");

            if (confirmed) {
                setSubmitting(true);
                const { justification } = data;

                // create form data
                const fd = new FormData();
                fd.append('justification', justification || "");

                await EmployeeLeaveService.reject(props.leaveId, fd);

                banner.add({
                    key: 'reject_employee_leave_success',
                    variant: 'success',
                    icon: faCheck,
                    text: `Leave request has been rejected.`
                });
                props.onDismissed(true, 'rejected');
            }
        } catch (e) {
            setSubmitting(false);
        }
    }

    return <Panel headerText={'Reject Leave'}
        isOpen={true}
        type={PanelType.medium}
        onDismiss={() => props.onDismissed(false)}
        isFooterAtBottom={true}
        onRenderFooterContent={() => {
            return <Stack horizontal tokens={{ childrenGap: 10 }}>
                {
                    !submitting ? (
                        <>
                            <PrimaryButton text={"Reject"} disabled={isSubmitButtonDisabled()} onClick={_onSubmit} />
                            <DefaultButton text={"Cancel"} onClick={() => { props.onDismissed(false) }} />
                        </>
                    ) : null
                }
                {submitting ? <Spinner size={SpinnerSize.medium} labelPosition={"right"} label={"Rejecting leave ..."} /> : null}
            </Stack>;
        }}>
        <Stack tokens={{ childrenGap: 15 }}>
            {!loaded ? <Stack horizontalAlign={"baseline"}><Spinner size={SpinnerSize.medium} labelPosition={"right"} label={"Preparing form ..."} /></Stack> : null}
            {
                loaded && data.leave ? <>
                    <Text className={'color-red'}>!!! Note: once this leave is rejected, this rejection cannot be undo !!!</Text>
                    <SelectEmployee selected={data.leave.employee} disabled
                        label={'Employee'}
                        onChange={() => { }} />
                    <SelectSubsidiary selected={data.leave.subsidiary} disabled
                        label={'Subsidiary'}
                        onChange={() => {}} />
                    <Stack className='divider'></Stack>
                    <Details text={EmployeeLeaveTypes.find((t) => t.key === data.leave?.type)?.text || "-"} title={'Leave type'} />
                    <Details title={'Date'} text={`${moment(data.leave.startDate).format("DD/MM/YYYY")} - ${moment(data.leave.endDate).format("DD/MM/YYYY")}`} />
                    <Details text={data.leave.reason} title={'Reason'} />
                    <Stack className='divider'></Stack>
                    <TextField label={"Justification"}
                        value={data.justification}
                        multiline
                        required
                        rows={5}
                        autoAdjustHeight
                        resizable={false}
                        onChange={(evt: any, value?: string) => {
                            let _data = data;
                            _data.justification = value;

                            const validation = ValidationService.combination(value, ['limit'], {maxChars: 2000});
                            error.reason = validation.message;

                            setData({ ...data });
                            setError({ ...error });
                        }} />
                </> : null
            }
        </Stack>
    </Panel>
};

export default RejectEmployeeLeaveForm;
